<script setup>
import FormGroup from "@/components/molecules/form-group";
import Button from "@/components/atoms/button";
import FormInput from "@/components/molecules/form-input";
import Form from "@/components/molecules/form";
import StateOverlay from "@/components/molecules/state-overlay";
import Popin from "@/containers/popin-adaptive";
import CarteKdoFindInfo from "@/components/organisms/payment-method-carte-kdo/carte-kdo-find-info";
import CarteKdoError from "./error";
import {
  formValidationProps,
  useFormValidation
} from "@/components/organisms/mixins/form-validation.mixin";
import { required, maxLength, minLength } from "@vuelidate/validators";
import useVuelidate from "@vuelidate/core";
import { StatusTypes } from "@/store/checkout";

const props = defineProps({
  checkoutStatus: {
    type: Object,
    required: false,
    default: () => {}
  },
  ...formValidationProps()
});

const emit = defineEmits(["checkCarteKdo"]);

const { states, getState, getErrorMessage, checkValidation } =
  useFormValidation(props);

const numero = ref("");
const pin = ref("");

const errorPopin = ref(null);

const validations = computed(() => ({
  numero: {
    required,
    maxLength: maxLength(21),
    minLength: minLength(21)
  },
  pin: {
    required,
    maxLength: maxLength(4),
    minLength: minLength(4)
  }
}));

const v$ = useVuelidate(
  validations,
  { numero, pin },
  { $stopPropagation: true }
);

const checkCarteKdo = () => {
  if (checkValidation(v$.value)) {
    emit("checkCarteKdo", {
      carte_cadeau: { numero: numero.value, pin: pin.value }
    });
  }
};

const carteInvalide = computed(() => {
  return props.error?.code === 404;
});

const state = computed(() => {
  switch (props.checkoutStatus?.type) {
    case StatusTypes.CHECKOUT_REQUEST:
      return states.LOADING;
    case StatusTypes.CHECKOUT_SUCCESS:
      return states.SUCCESS;
    case StatusTypes.CHECKOUT_ERROR:
      return states.ERROR;
    default:
      return states.DEFAULT;
  }
});

watch(
  () => state.value,
  () => {
    if (
      state.value === states.ERROR &&
      (props.error.code !== 422 ||
        !props.error.errors ||
        props.error.errors.length === 0) &&
      props.error.message !== "Checkout inconnu"
    ) {
      errorPopin.value?.show();
    }
  }
);
</script>

<template>
  <div class="c-payment-method-carte-kdo-number-form">
    <StateOverlay
      :is-loading="state === states.LOADING"
      vertical-align
      overlay
      loading-spinner-size="xs"
      message="Nous vérifions la validité de la carte cadeau"
      class="u-full-height"
    >
      <div class="c-payment-method-carte-kdo-number-form__content">
        <div class="u-flex u-gap-sm u-pb-md u-flex-column@lg u-flex-align-start@lg u-gap-xs@lg">
          <p class="u-font-secondary u-font-18 u-font-700">
            &nbsp;J'entre les informations de ma Carte Cadeau Cora
          </p>
          <Button
            link
            @click="$refs.infoPopin.show()"
          >
            Où trouver ces informations sur ma carte ?
          </Button>
        </div>
        <Form
          class="u-flex u-flex-align-start u-full-width"
          @submit.prevent="checkCarteKdo"
        >
          <div class="c-payment-method-carte-kdo-number-form__form u-full-width">
            <FormGroup
              :state="getState(v$, 'numero')"
              size="sm"
              class="c-payment-method-carte-kdo-number-form__input u-full-width u-pb-sm"
            >
              <FormInput
                id="numero"
                v-model="numero"
                :state="getState(v$, 'numero')"
                size="sm"
                @blur="v$.numero.$touch()"
                @input="v$.numero.$reset()"
              >
                N° de la carte
              </FormInput>
              <template #message>
                <p class="u-text-wrap-balance u-full-width u-max-width-30">
                  <template v-if="v$.numero.required.$invalid">
                    Merci de renseigner le numéro de votre carte cadeau
                  </template>
                  <template v-else-if="v$.numero.maxLength.$invalid || v$.numero.minLength.$invalid">
                    <p>Le numéro de carte doit être composé de 21 chiffres</p>
                  </template>
                  <template v-else>
                    {{ getErrorMessage("numero", "Le numéro de carte est invalide. Merci de vérifier votre saisie") }}
                  </template>
                </p>
              </template>
            </FormGroup>
            <FormGroup
              :state="getState(v$, 'pin')"
              size="xs"
              class="c-payment-method-carte-kdo-number-form__input u-full-width u-pb-md"
            >
              <FormInput
                id="pin"
                v-model="pin"
                :state="getState(v$, 'pin')"
                size="sm"
                @blur="v$.pin.$touch()"
                @input="v$.pin.$reset()"
              >
                Code Pin
              </FormInput>
              <template #message>
                <p class="u-text-wrap-balance u-full-width u-max-width-30">
                  <template v-if="v$.pin.required.$invalid">
                    Merci de renseigner le code pin de votre carte cadeau
                  </template>
                  <template v-else-if="v$.pin.maxLength.$invalid || v$.pin.minLength.$invalid">
                    <p>Le code pin doit être composé de 4 chiffres</p>
                  </template>
                  <template v-else>
                    {{ getErrorMessage("pin", "Le code pin est invalide. Merci de vérifier votre saisie") }}
                  </template>
                </p>
              </template>
            </FormGroup>
            <Button
              size="md"
              radius="full"
              variant="secondary"
              :disabled="v$.$invalid"
              type="submit"
              value="Valider"
            />
          </div>
        </Form>
      </div>
    </StateOverlay>
    <Popin
      ref="infoPopin"
      size="lg"
    >
      <CarteKdoFindInfo />
    </Popin>
    <Popin
      ref="errorPopin"
      size="lg"
    >
      <CarteKdoError
        :carte-invalide="carteInvalide"
        @close="errorPopin?.hide()"
      />
    </Popin>
  </div>
</template>

<style lang="scss" scoped>
</style>
